import React, { useState } from 'react';
import { Download, X, QrCode } from 'lucide-react';
import api from '../services/api';

interface FairQRCodeProps {
  fairId: number;
  fairName: string;
  allowDownload?: boolean; // If false, only shows preview (no download functionality)
}

type QRSize = 'small' | 'medium' | 'large';

const QR_SIZE_MAP: Record<QRSize, { pixels: number; label: string }> = {
  small: { pixels: 256, label: 'Small (256x256)' },
  medium: { pixels: 512, label: 'Medium (512x512)' },
  large: { pixels: 1024, label: 'Large (1024x1024)' },
};

export default function FairQRCode({ fairId, fairName, allowDownload = true }: FairQRCodeProps) {
  const [showModal, setShowModal] = useState(false);
  const [selectedSize, setSelectedSize] = useState<QRSize>('medium');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getQRCodeUrl = (size: QRSize) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    // Use public endpoint for viewing (no authentication required)
    return `${baseUrl}/fairs/${fairId}/qr-code?size=${size}`;
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('Please log in to download QR code');
        return;
      }

      const response = await api.get(`/owner/fairs/${fairId}/qr-code?size=${selectedSize}`, {
        responseType: 'blob',
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'image/png' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fair_${fairId}_qr_code_${selectedSize}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setLoading(false);
    } catch (err: unknown) {
      console.error('Failed to download QR code:', err);
      setError('Failed to download QR code. Please try again.');
      setLoading(false);
    }
  };

  return (
    <>
      {/* QR Code Button */}
      <button
        onClick={() => setShowModal(true)}
        className="w-full flex items-center justify-center gap-2 px-3 py-1.5 bg-primary hover:bg-primary-dark text-white rounded transition-colors text-xs sm:text-sm"
        title={allowDownload ? "View & Download QR Code" : "View QR Code"}
      >
        <QrCode className="h-4 w-4" />
        <span>{allowDownload ? "QR Code" : "View QR Code"}</span>
      </button>

      {/* QR Code Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">QR Code for {fairName}</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Size Selector - Only for download mode */}
              {allowDownload && (
                <div className="mb-6">
                  <label htmlFor="qr-size" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Size
                  </label>
                  <select
                    id="qr-size"
                    value={selectedSize}
                    onChange={(e) => setSelectedSize(e.target.value as QRSize)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {Object.entries(QR_SIZE_MAP).map(([size, { label }]) => (
                      <option key={size} value={size}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-sm text-gray-500">
                    Choose the size based on your printing needs. Larger sizes provide better quality for
                    printing on posters or signs.
                  </p>
                </div>
              )}

              {/* QR Code Preview */}
              <div className="bg-gray-50 rounded-lg p-8 flex flex-col items-center justify-center mb-6">
                <div className="bg-white p-4 rounded-lg shadow-md">
                  <img
                    src={getQRCodeUrl(selectedSize)}
                    alt={`QR Code for ${fairName}`}
                    className="max-w-full h-auto"
                    style={{ maxWidth: '400px' }}
                    onError={(e) => {
                      console.error('Failed to load QR code image:', getQRCodeUrl(selectedSize));
                      console.error('Error event:', e);
                      setError('Failed to load QR code image. Please try refreshing the page.');
                    }}
                    onLoad={() => {
                      console.log('QR code image loaded successfully:', getQRCodeUrl(selectedSize));
                    }}
                  />
                </div>
                <p className="mt-4 text-center text-sm text-gray-600 font-medium">{fairName}</p>
                <p className="mt-2 text-center text-xs text-gray-500">
                  Scan to view artist fair details
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                  {error}
                </div>
              )}

              {/* Action Buttons */}
              {allowDownload ? (
                <div className="flex gap-3">
                  <button
                    onClick={handleDownload}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary-dark disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
                  >
                    <Download className="h-5 w-5" />
                    {loading ? 'Downloading...' : 'Download PNG'}
                  </button>
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="flex justify-center">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-8 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}

              {/* Printing Instructions - Only for download mode */}
              {allowDownload && (
                <div className="mt-6 p-4 bg-moonbone border border-teal-200 rounded-lg">
                  <h3 className="font-semibold text-ink mb-2">Printing Tips</h3>
                  <ul className="text-sm text-ink space-y-1">
                    <li>• Use high-quality photo paper for best results</li>
                    <li>• Large size (1024x1024) recommended for posters and signs</li>
                    <li>• Medium size (512x512) works well for flyers and handouts</li>
                    <li>• Small size (256x256) suitable for business cards</li>
                    <li>• Test scan the QR code before mass printing</li>
                    <li>• Ensure good contrast (dark QR code on light background)</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
